// Progressively enhances the PDP (rendered in main-product.liquid).
//
// Server-rendered state is already correct for the initial variant, so
// this module only has to react to user input: variant swatch changes,
// thumbnail clicks, the main-image hover arrows, the thumb strip's
// paging arrows, the quantity steppers, and the description expander.
// It reads the full product payload from the PdpProductData JSON script
// tag (Shopify's `json` filter escapes forward slashes, so a stray
// "</script>" in a title/description can't break out of the tag) rather
// than re-fetching anything, matching the variant purely by comparing
// each variant's `options` array against the checked swatch radios (a
// no-op for single-variant products, since no swatches are rendered).
//
// The form itself is untouched: it is Shopify's canonical `product`
// form and posts straight to /cart/add. This module's only job is to
// keep the hidden `id` input, price, availability, and gallery in sync
// with whatever the shopper has selected.
//
// It also reveals the sticky mobile add-to-cart bar (#PdpBar) once the
// primary add-to-cart button scrolls out of view on narrow viewports,
// and mirrors the bar's price/availability whenever the variant
// changes. The bar's own button submits the same #PdpForm via its
// `form` attribute, so there is no second form to keep in sync.

const dataEl = document.getElementById('PdpProductData');
const form = document.getElementById('PdpForm');

function parseProduct(el) {
  try {
    return JSON.parse(el.textContent);
  } catch (error) {
    return null;
  }
}

// Description expander: independent of the product payload so it works
// even if the JSON ever fails to parse.
const desc = document.querySelector('[data-desc]');
if (desc) {
  const body = desc.querySelector('[data-desc-body]');
  const toggle = desc.querySelector('[data-desc-toggle]');
  if (body && toggle) {
    // No fade or chevron when the whole description already fits.
    if (body.scrollHeight <= body.clientHeight + 4) {
      desc.classList.add('pdp__desc--short');
    }
    toggle.addEventListener('click', () => {
      const open = desc.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', String(open));
      toggle.setAttribute(
        'aria-label',
        open ? 'Collapse description' : 'Show full description'
      );
    });
  }
}

// Quantity steppers.
const qtyInput = document.getElementById('PdpQuantity');
if (qtyInput) {
  const stepBy = (delta) => {
    const current = parseInt(qtyInput.value, 10);
    const next = (Number.isNaN(current) ? 1 : current) + delta;
    qtyInput.value = String(Math.max(1, next));
  };
  const up = document.querySelector('[data-qty-up]');
  const down = document.querySelector('[data-qty-down]');
  if (up) up.addEventListener('click', () => stepBy(1));
  if (down) down.addEventListener('click', () => stepBy(-1));
}

if (dataEl && form) {
  const product = parseProduct(dataEl);

  if (product && Array.isArray(product.variants)) {
    const variantIdInput = document.getElementById('PdpVariantId');
    const priceEl = document.getElementById('PdpPrice');
    const submitButton = document.getElementById('PdpAddToCart');
    const galleryTrack = document.querySelector('[data-gallery-track]');
    const slides = galleryTrack
      ? Array.from(galleryTrack.querySelectorAll('.pdp__slide'))
      : [];
    const optionInputs = Array.from(form.querySelectorAll('input[data-option-index]'));
    const thumbs = Array.from(document.querySelectorAll('.pdp__thumb'));

    const bar = document.getElementById('PdpBar');
    const barPriceEl = bar ? document.getElementById('PdpBarPrice') : null;
    const barButton = bar ? document.getElementById('PdpBarAddToCart') : null;

    const currency = (priceEl && priceEl.getAttribute('data-currency')) || undefined;
    let formatter = null;
    if (currency) {
      try {
        formatter = new Intl.NumberFormat(undefined, { style: 'currency', currency });
      } catch (error) {
        formatter = null;
      }
    }

    function formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      if (formatter) return formatter.format(amount);
      return `$${amount.toFixed(2)}`;
    }

    function currentSelections() {
      const selections = [];
      optionInputs.forEach((input) => {
        if (!input.checked) return;
        selections[Number(input.getAttribute('data-option-index'))] = input.value;
      });
      return selections;
    }

    function findVariant(selections) {
      return product.variants.find((variant) => {
        if (!Array.isArray(variant.options)) return false;
        return variant.options.every((value, index) => value === selections[index]);
      });
    }

    function setActiveThumb(mediaId) {
      thumbs.forEach((thumb) => {
        const isActive =
          mediaId != null && String(thumb.getAttribute('data-media-id')) === String(mediaId);
        thumb.classList.toggle('pdp__thumb--active', isActive);
        if (isActive) {
          thumb.setAttribute('aria-current', 'true');
          // Scroll the strip itself, never scrollIntoView: that one also
          // scrolls the page vertically, which reads as the buy box moving.
          const strip = thumb.parentElement;
          if (strip && strip.scrollWidth > strip.clientWidth) {
            const centered =
              thumb.offsetLeft - (strip.clientWidth - thumb.offsetWidth) / 2;
            strip.scrollLeft = Math.max(0, centered);
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    }

    // The gallery is a native scroll-snap track: "showing" an image just
    // means scrolling its slide into place. Instant on first paint, smooth
    // for every user-driven change after that.
    //
    // The track carries a clone of the last image before the first and a
    // clone of the first after the last. Swiping off either end lands on a
    // clone, and once the scroll settles we jump instantly to its real
    // twin — so the gallery keeps going round instead of hitting a wall.
    // `slides` stays the real slides only; clones are never addressed.
    let headClone = null;
    let tailClone = null;
    if (galleryTrack && slides.length > 1) {
      const head = slides[slides.length - 1].cloneNode(true);
      const tail = slides[0].cloneNode(true);
      headClone = head;
      tailClone = tail;
      [head, tail].forEach((clone) => {
        clone.setAttribute('data-clone', 'true');
        clone.removeAttribute('data-media-id');
        clone.querySelectorAll('img').forEach((img) => {
          img.setAttribute('aria-hidden', 'true');
        });
      });
      galleryTrack.insertBefore(head, slides[0]);
      galleryTrack.appendChild(tail);
      // Insert-then-position in the same task: the leading clone shifts
      // everything right, so scrollLeft must move with it before paint.
      galleryTrack.style.scrollBehavior = 'auto';
      galleryTrack.scrollLeft = slides[0].offsetLeft;
      galleryTrack.style.scrollBehavior = '';
    }

    function slideIndexForMedia(mediaId) {
      return slides.findIndex(
        (slide) => String(slide.getAttribute('data-media-id')) === String(mediaId)
      );
    }

    // Assigning scrollLeft (rather than scrollTo with a behavior option)
    // lets the track's CSS scroll-behavior decide, which also means the
    // reduced-motion override applies for free.
    function showSlide(index, instant) {
      if (!galleryTrack || slides.length === 0) return;
      const slide = slides[(index + slides.length) % slides.length];
      if (!slide) return;
      if (instant) galleryTrack.style.scrollBehavior = 'auto';
      galleryTrack.scrollLeft = slide.offsetLeft;
      if (instant) galleryTrack.style.scrollBehavior = '';
      setActiveThumb(slide.getAttribute('data-media-id'));
    }

    // Real index of whatever the track is currently sitting on. Returns
    // -1 on the leading clone and slides.length on the trailing one, so
    // callers can tell "past the end" from "at the end".
    function currentSlideIndex() {
      if (!galleryTrack || slides.length === 0) return 0;
      const width = galleryTrack.clientWidth || 1;
      const domIndex = Math.round(galleryTrack.scrollLeft / width);
      return slides.length > 1 ? domIndex - 1 : domIndex;
    }

    function swapMainImage(thumb) {
      if (!thumb) return;
      const index = slideIndexForMedia(thumb.getAttribute('data-media-id'));
      if (index >= 0) showSlide(index);
    }

    function updateImageForVariant(variant) {
      const media = variant && variant.featured_media;
      if (!media || media.media_type !== 'image') return;
      const index = slideIndexForMedia(media.id);
      if (index < 0) return;
      showSlide(index);
    }

    function updatePrice(variant) {
      if (!priceEl) return;
      const hasCompareAt =
        variant.compare_at_price && variant.compare_at_price > variant.price;
      if (hasCompareAt) {
        priceEl.innerHTML =
          '<span class="price">' +
          `<s class="price__compare-at">${formatMoney(variant.compare_at_price)}</s> ` +
          `<span class="price__current">${formatMoney(variant.price)}</span>` +
          '</span>';
      } else {
        priceEl.innerHTML =
          `<span class="price"><span class="price__current">${formatMoney(variant.price)}</span></span>`;
      }
    }

    function syncBar() {
      if (!bar) return;
      if (barPriceEl && priceEl) barPriceEl.innerHTML = priceEl.innerHTML;
      if (barButton && submitButton) {
        barButton.disabled = submitButton.disabled;
        barButton.textContent = submitButton.textContent;
      }
    }

    function updateUrl(variant) {
      if (!variant || !window.history || !window.history.replaceState) return;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    function applyVariant(variant) {
      if (!variant) {
        if (variantIdInput) variantIdInput.value = '';
        if (submitButton) {
          submitButton.disabled = true;
          submitButton.textContent = 'Unavailable';
        }
        syncBar();
        return;
      }

      if (variantIdInput) variantIdInput.value = String(variant.id);
      updatePrice(variant);
      updateImageForVariant(variant);
      updateUrl(variant);
      if (submitButton) {
        submitButton.disabled = !variant.available;
        submitButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      syncBar();
    }

    function handleOptionChange() {
      applyVariant(findVariant(currentSelections()));
    }

    optionInputs.forEach((input) => {
      input.addEventListener('change', handleOptionChange);
    });

    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        swapMainImage(thumb);
        setActiveThumb(thumb.getAttribute('data-media-id'));
      });
    });

    // Every gallery image is a real <img> in the track and loads eagerly,
    // so there is nothing left to warm: swipes never wait on the network.

    if (galleryTrack && slides.length > 1) {
      // Land on the variant-selected image before first paint. 'auto'
      // (not smooth) so it reads as the starting position, not a jump.
      const initialIndex = slideIndexForMedia(
        (function () {
          const active = thumbs.find((thumb) =>
            thumb.classList.contains('pdp__thumb--active')
          );
          return active ? active.getAttribute('data-media-id') : null;
        })()
      );
      if (initialIndex > 0) showSlide(initialIndex, true);

      // Hover arrows step the track (hidden by CSS on touch devices,
      // where the swipe itself is the control).
      // Stepping off either end goes to the adjacent clone rather than
      // racing the whole track back, so the arrows loop as smoothly as a
      // swipe does; the settle handler then swaps in the real slide.
      const galleryPrev = document.querySelector('[data-gallery-prev]');
      const galleryNext = document.querySelector('[data-gallery-next]');
      if (galleryPrev) {
        galleryPrev.addEventListener('click', () => {
          const index = currentSlideIndex();
          if (index <= 0 && headClone) galleryTrack.scrollLeft = headClone.offsetLeft;
          else showSlide(index - 1);
        });
      }
      if (galleryNext) {
        galleryNext.addEventListener('click', () => {
          const index = currentSlideIndex();
          if (index >= slides.length - 1 && tailClone) {
            galleryTrack.scrollLeft = tailClone.offsetLeft;
          } else {
            showSlide(index + 1);
          }
        });
      }

      // Once the track stops moving, hop off a clone onto its real twin.
      // Instant and at rest, so the swipe just keeps going round.
      let settleTimer = null;
      const settle = () => {
        const index = currentSlideIndex();
        if (index < 0) showSlide(slides.length - 1, true);
        else if (index >= slides.length) showSlide(0, true);
      };
      const queueSettle = () => {
        if (settleTimer) clearTimeout(settleTimer);
        settleTimer = setTimeout(settle, 120);
      };
      if ('onscrollend' in window) {
        galleryTrack.addEventListener('scrollend', settle, { passive: true });
      } else {
        galleryTrack.addEventListener('scroll', queueSettle, { passive: true });
      }

      // Follow the swipe: highlight the thumb the track settled on.
      let scrollFrame = null;
      let lastSyncedIndex = -1;
      galleryTrack.addEventListener(
        'scroll',
        () => {
          if (scrollFrame) return;
          scrollFrame = requestAnimationFrame(() => {
            scrollFrame = null;
            const index = currentSlideIndex();
            if (index === lastSyncedIndex) return;
            lastSyncedIndex = index;
            // Clones borrow the thumb of the real slide they stand in for.
            const slide =
              slides[index < 0 ? slides.length - 1 : index % slides.length];
            if (slide) setActiveThumb(slide.getAttribute('data-media-id'));
          });
        },
        { passive: true }
      );
    }

    // Pinch to zoom the image under the fingers. Two fingers only, so a
    // one-finger swipe or page scroll is never touched. The image follows
    // the gesture live and springs back to its box on release — nothing
    // to undo, and no zoom ceiling.
    //
    // `touch-action:pan-x pan-y` on the image (CSS) is what makes this
    // possible: it leaves both scroll directions to the browser while
    // handing pinch to us, so preventDefault here never blocks scrolling.
    if (galleryTrack) {
      let zoomImg = null;
      let startDist = 0;
      let startMidX = 0;
      let startMidY = 0;

      const spread = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
      };
      const midpoint = (touches) => ({
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
      });

      const endZoom = () => {
        if (!zoomImg) return;
        const img = zoomImg;
        zoomImg = null;
        galleryTrack.classList.remove('is-zooming');
        img.style.transition = 'transform 220ms ease-out';
        img.style.transform = '';
        setTimeout(() => {
          img.style.transition = '';
          img.style.transformOrigin = '';
          img.style.zIndex = '';
        }, 240);
      };

      galleryTrack.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 2) return;
          const img = event.target.closest('.pdp__slide-img');
          if (!img) return;
          zoomImg = img;
          startDist = spread(event.touches) || 1;
          const mid = midpoint(event.touches);
          const box = img.getBoundingClientRect();
          startMidX = mid.x;
          startMidY = mid.y;
          // Zoom out of the point between the fingers, not the centre.
          img.style.transformOrigin =
            ((mid.x - box.left) / box.width) * 100 + '% ' +
            ((mid.y - box.top) / box.height) * 100 + '%';
          img.style.transition = '';
          img.style.zIndex = '3';
          galleryTrack.classList.add('is-zooming');
        },
        { passive: true }
      );

      galleryTrack.addEventListener(
        'touchmove',
        (event) => {
          if (!zoomImg || event.touches.length !== 2) return;
          event.preventDefault();
          const scale = Math.max(0.5, spread(event.touches) / startDist);
          const mid = midpoint(event.touches);
          zoomImg.style.transform =
            'translate(' + (mid.x - startMidX) + 'px,' + (mid.y - startMidY) + 'px) ' +
            'scale(' + scale + ')';
        },
        { passive: false }
      );

      galleryTrack.addEventListener('touchend', endZoom, { passive: true });
      galleryTrack.addEventListener('touchcancel', endZoom, { passive: true });
      // iOS Safari zooms the whole page off its own gesture events unless
      // they are cancelled here as well.
      ['gesturestart', 'gesturechange', 'gestureend'].forEach((name) => {
        galleryTrack.addEventListener(name, (event) => event.preventDefault());
      });
    }

    // Thumb strip paging: arrows and edge fades appear only on the sides
    // that still have thumbs hiding beyond them.
    const thumbsWrap = document.querySelector('[data-thumbs-wrap]');
    if (thumbsWrap) {
      const strip = thumbsWrap.querySelector('[data-thumbs]');
      const prevArrow = thumbsWrap.querySelector('[data-thumbs-prev]');
      const nextArrow = thumbsWrap.querySelector('[data-thumbs-next]');
      if (strip && prevArrow && nextArrow) {
        const update = () => {
          const maxScroll = strip.scrollWidth - strip.clientWidth - 1;
          const hasPrev = strip.scrollLeft > 2;
          const hasNext = strip.scrollLeft < maxScroll;
          thumbsWrap.classList.toggle('has-prev', hasPrev);
          thumbsWrap.classList.toggle('has-next', hasNext);
          prevArrow.hidden = !hasPrev;
          nextArrow.hidden = !hasNext;
        };
        const page = (direction) => {
          strip.scrollBy({ left: direction * strip.clientWidth * 0.8 });
        };
        prevArrow.addEventListener('click', () => page(-1));
        nextArrow.addEventListener('click', () => page(1));
        strip.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        update();
      }
    }

    // Sticky mobile add-to-cart bar: revealed once the main add-to-cart
    // button has scrolled out of view, and only below the 750px
    // breakpoint (the bar's own CSS also hides it at/above that width,
    // this just avoids observing/toggling pointlessly on desktop).
    // While the bar is visible, the body gets matching bottom padding so
    // the fixed bar never occludes the end of the page (footer links,
    // newsletter button); the padding is cleared whenever the bar hides,
    // and is never applied on desktop since the bar only reveals when
    // the mobile media query matches.
    if (bar && submitButton && 'IntersectionObserver' in window) {
      syncBar();

      const mobileQuery = window.matchMedia('(max-width: 749px)');

      function setBarVisible(visible) {
        bar.hidden = !visible;
        document.body.style.paddingBottom = visible ? `${bar.offsetHeight}px` : '';
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            setBarVisible(mobileQuery.matches && !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      observer.observe(submitButton);

      const handleMediaChange = () => {
        if (!mobileQuery.matches) setBarVisible(false);
      };
      if (typeof mobileQuery.addEventListener === 'function') {
        mobileQuery.addEventListener('change', handleMediaChange);
      } else if (typeof mobileQuery.addListener === 'function') {
        mobileQuery.addListener(handleMediaChange);
      }
    }
  }
}
